import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  transcribeUploadedAudio,
  getVoskAcceptAttribute,
  validateVoskAudioFile,
  VOSK_MAX_AUDIO_BYTES,
  VOSK_SERVICE_ORIGIN,
} from "../../services/vosk/transcribeUploadedAudio";
import { formatBytes } from "./uploaderFiles";

// AudioUploader
//
// A drop-in widget for uploading audio files (mp3, wav, m4a, webm, ogg, flac,
// mp4, …) and getting them transcribed by the in-browser Vosk service hosted
// at `VOSK_SERVICE_ORIGIN`. It replaces the deprecated server-side
// `/corti/transcribe-recorded/?transcription_model=vosk` endpoint with the
// browser-only flow.
//
// Props:
//   onTranscript(text, meta)   Optional. Called with `{ text, durationSeconds,
//                                modelId, fileName }` after a successful
//                                transcription. Use it to wire the result
//                                into a patient record, the Corti/Vosk SOAP
//                                modal, or wherever downstream logic lives.
//   onUpload(file, meta)       Optional. Called with the picked `File` as soon
//                                as the user chooses it (no transcription yet).
//                                Lets a parent save the audio to storage
//                                immediately and patch the transcript in
//                                afterwards.
//   defaultFile                Optional. Pre-seed the widget with a File
//                                (e.g. one already chosen elsewhere on the
//                                page). The user just clicks "Transcribe".
//   buttonLabel / renderTrigger  Optional trigger customization, mirroring
//                                `Uploader`'s API.
//   autoCloseOnSuccess         When true, dismiss the modal as soon as a
//                                transcript is produced. Default `false` so
//                                the user can copy/paste the transcript.

const AudioUploader = ({
  onTranscript,
  onUpload,
  defaultFile = null,
  renderTrigger,
  buttonLabel = "Upload & Transcribe Audio",
  modalTitle = "Upload audio & transcribe with Vosk",
  autoCloseOnSuccess = false,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const inputRef = useRef(null);
  const [file, setFile] = useState(defaultFile);
  const [isDragging, setIsDragging] = useState(false);
  const [status, setStatus] = useState("");
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("idle"); // loading-model|decoding|transcribing|finalizing|done|idle|error
  const [secondsProcessed, setSecondsProcessed] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [etaSeconds, setEtaSeconds] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [error, setError] = useState("");

  const reset = () => {
    setFile(null);
    setStatus("");
    setProgress(0);
    setPhase("idle");
    setSecondsProcessed(0);
    setTotalSeconds(0);
    setEtaSeconds(null);
    setIsProcessing(false);
    setTranscript("");
    setDurationSeconds(0);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  // Human-readable label for the current phase, used both as the
  // progress-bar caption and the status line below it.
  const PHASE_LABEL = {
    idle: "Idle",
    "loading-model": "Loading Vosk model",
    decoding: "Decoding audio",
    transcribing: "Transcribing",
    finalizing: "Finalizing",
    done: "Done",
    error: "Error",
  };
  const formatEta = (secs) => {
    if (secs == null || !Number.isFinite(secs)) return null;
    if (secs < 60) return `~${Math.max(1, Math.round(secs))}s left`;
    const m = Math.floor(secs / 60);
    const s = Math.round(secs % 60);
    return `~${m}m ${s.toString().padStart(2, "0")}s left`;
  };
  const formatClock = (secs) => {
    if (!Number.isFinite(secs) || secs < 0) return "0:00";
    const total = Math.round(secs);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleClose = () => {
    if (isProcessing) return; // don't let the user yank a transcription out
    reset();
    onClose();
  };

  const pickFile = (incoming) => {
    if (!incoming || !incoming.length) return;
    const f = incoming[0];
    try {
      validateVoskAudioFile(f);
    } catch (e) {
      toast({
        title: "Cannot use that audio file",
        description: e.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setFile(f);
    setTranscript("");
    setError("");
    setProgress(0);
    setDurationSeconds(0);
    setStatus(`Selected ${f.name} (${formatBytes(f.size)})`);
    if (typeof onUpload === "function") {
      try {
        onUpload(f, { fileName: f.name, size: f.size });
      } catch (e) {
        console.warn("[AudioUploader] onUpload callback threw:", e);
      }
    }
  };

  const handleFileChange = (event) => {
    pickFile(event.target.files);
    event.target.value = "";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    pickFile(event.dataTransfer?.files);
  };

  const handleTranscribe = async () => {
    if (!file) return;
    setIsProcessing(true);
    setError("");
    setProgress(0);
    setPhase("decoding");
    setSecondsProcessed(0);
    setTotalSeconds(0);
    setEtaSeconds(null);
    setStatus("Decoding audio…");
    setTranscript("");
    try {
      const result = await transcribeUploadedAudio(file, {
        onStatus: setStatus,
        onProgress: (p) => {
          if (p?.phase) setPhase(p.phase);
          const ratio = Math.max(0, Math.min(1, Number(p?.ratio) || 0));
          setProgress(ratio);
          if (typeof p?.secondsProcessed === "number") {
            setSecondsProcessed(p.secondsProcessed);
          }
          if (typeof p?.totalSeconds === "number" && p.totalSeconds > 0) {
            setTotalSeconds(p.totalSeconds);
          }
          if (p?.etaSeconds !== undefined) setEtaSeconds(p.etaSeconds);
        },
      });
      const text = (result?.text || "").trim();
      setTranscript(text);
      setDurationSeconds(result?.durationSeconds || 0);
      setTotalSeconds(result?.durationSeconds || totalSeconds);
      setSecondsProcessed(result?.durationSeconds || totalSeconds);
      setStatus(
        text
          ? `Done — ${text.length} chars, ${Math.round(result?.durationSeconds || 0)}s of audio.`
          : "Done — no speech detected.",
      );
      setPhase("done");
      setProgress(1);
      setEtaSeconds(null);
      if (typeof onTranscript === "function") {
        try {
          onTranscript(text, {
            durationSeconds: result?.durationSeconds || 0,
            modelId: result?.modelId,
            serviceOrigin: result?.serviceOrigin,
            fileName: file.name,
            fileSize: file.size,
          });
        } catch (e) {
          console.warn("[AudioUploader] onTranscript callback threw:", e);
        }
      }
      if (text) {
        toast({
          title: "Transcription complete",
          description: `${text.length} characters from ${file.name}`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "No speech detected",
          description:
            "Vosk returned an empty transcript. Try a clearer recording or a different file.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      }
      if (autoCloseOnSuccess) {
        setTimeout(() => {
          reset();
          onClose();
        }, 600);
      }
    } catch (e) {
      console.error("[AudioUploader] transcription failed:", e);
      setError(e?.message || "Transcription failed.");
      setStatus("");
      setProgress(0);
      setPhase("error");
      toast({
        title: "Transcription failed",
        description: e?.message || "Unknown error",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ onClick: onOpen })
      ) : (
        <Tooltip
          label={`Pick an audio file and transcribe it in your browser via ${VOSK_SERVICE_ORIGIN}.`}
          placement="top"
        >
          <Button onClick={onOpen} colorScheme="blue" size="sm">
            {buttonLabel}
          </Button>
        </Tooltip>
      )}

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        closeOnOverlayClick={!isProcessing}
        closeOnEsc={!isProcessing}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{modalTitle}</ModalHeader>
          <ModalCloseButton isDisabled={isProcessing} />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Box
                as="label"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                cursor={isProcessing ? "not-allowed" : "pointer"}
                borderWidth="2px"
                borderStyle="dashed"
                borderRadius="md"
                px={4}
                py={6}
                transition="all 0.15s"
                borderColor={isDragging ? "blue.400" : "gray.300"}
                bg={isDragging ? "blue.50" : "gray.50"}
                _hover={
                  isProcessing
                    ? {}
                    : { borderColor: "blue.400", bg: "blue.50" }
                }
                opacity={isProcessing ? 0.6 : 1}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept={getVoskAcceptAttribute()}
                  onChange={handleFileChange}
                  disabled={isProcessing}
                  style={{ display: "none" }}
                />
                <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                  {file
                    ? `Selected: ${file.name}`
                    : "Drag & drop an audio file here"}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {file
                    ? `${formatBytes(file.size)} • ${file.type || "unknown type"}`
                    : "or click to browse"}
                </Text>
                <Text fontSize="xs" color="gray.400" mt={1}>
                  mp3, wav, m4a, webm, ogg, flac, mp4 (max{" "}
                  {Math.round(VOSK_MAX_AUDIO_BYTES / 1024 / 1024)} MB)
                </Text>
              </Box>

              {status && (
                <Flex align="center" gap={2}>
                  <Text fontSize="xs" color="gray.600" flex="1" noOfLines={2}>
                    {status}
                  </Text>
                </Flex>
              )}

              {(isProcessing || progress > 0 || phase === "done") && (
                <Box>
                  <Flex justify="space-between" align="center" mb={1}>
                    <Text fontSize="xs" color="gray.700" fontWeight="semibold">
                      {PHASE_LABEL[phase] || "Working"}
                    </Text>
                    <HStack spacing={3} fontSize="xs" color="gray.500">
                      {totalSeconds > 0 && (
                        <Text>
                          {formatClock(secondsProcessed)} /{" "}
                          {formatClock(totalSeconds)}
                        </Text>
                      )}
                      {etaSeconds != null && phase !== "done" && (
                        <Text color="blue.600" fontWeight="medium">
                          {formatEta(etaSeconds)}
                        </Text>
                      )}
                      {phase === "done" && (
                        <Text color="green.600" fontWeight="medium">
                          100%
                        </Text>
                      )}
                    </HStack>
                  </Flex>
                  <Progress
                    value={Math.round(progress * 100)}
                    size="sm"
                    colorScheme={phase === "error" ? "red" : "blue"}
                    hasStripe={isProcessing}
                    isAnimated={isProcessing && phase !== "done"}
                  />
                </Box>
              )}

              {error && (
                <Text fontSize="sm" color="red.500">
                  {error}
                </Text>
              )}

              {transcript && (
                <Box>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Transcript ({transcript.length} chars
                    {durationSeconds
                      ? `, ${Math.round(durationSeconds)}s audio`
                      : ""}
                    ):
                  </Text>
                  <Textarea
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    rows={8}
                    fontSize="sm"
                    isDisabled={isProcessing}
                  />
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Text fontSize="xs" color="gray.500" mr="auto">
              Transcription runs entirely in your browser via Vosk
              {VOSK_SERVICE_ORIGIN ? ` (${VOSK_SERVICE_ORIGIN})` : ""}.
            </Text>
            <Button
              variant="ghost"
              mr={3}
              onClick={handleClose}
              isDisabled={isProcessing}
            >
              {transcript ? "Close" : "Cancel"}
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleTranscribe}
              isDisabled={!file || isProcessing}
              isLoading={isProcessing}
              loadingText="Transcribing…"
            >
              Transcribe with Vosk
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AudioUploader;